import { generateExercice, hasGenerator } from '../generators';
import { DEFIS_QUOTA_JOUR, DEFIS_MAX_JOUR } from './xpConfig';

/**
 * Défis quotidiens — moteur de tirage côté client.
 *
 * L'élève choisit un THÈME (nœuds de l'Île, données useTheme déjà chargées) ;
 * on pioche des exercices au hasard parmi les générateurs de son niveau
 * rattachés à ce thème. Le back crédite l'XP (source 'quiz_auto', barème
 * 10/7/3, quota 5/jour arbitré par contrainte DB) via
 * POST /defis-quotidiens/submit/ ; le badge « x/5 » se lit dans
 * profil.defis_credites_jour (réconciliation multi-appareils) avec un
 * compteur localStorage en optimistic UI / dégradation sans back.
 */

/** '6emeA' | '6ème A' → '6eme' (niveaux générateurs). Défaut : 6eme. */
export const niveauDepuisClasse = (classeNom) => {
    const c = String(classeNom || '')[0];
    return { 6: '6eme', 5: '5eme', 4: '4eme', 3: '3eme' }[c] || '6eme';
};

/**
 * Attendus d'un thème (useTheme) jouables en AUTONOMIE : rubrique
 * « automatisme » UNIQUEMENT (décision prof 2026-08-23) — l'espace élève en
 * autonomie ne sert que les automatismes du programme 2025, jamais les
 * objectifs d'apprentissage. Un attendu sans champ rubrique (vieux cache,
 * référentiel non migré) est un objectif (défaut back) : exclu — le filtre
 * ne peut que sur-bloquer. Filtre unique pour l'affichage (Île, régions) ET
 * le vivier de tirage : « thème affiché jouable » == « tirage possible »
 * reste garanti. Depuis septembre 2026, les automatismes de la 6e à la 3e
 * ont tous leurs générateurs : chaque niveau a ses défis.
 */
export const attendusJouables = (theme) => {
    if (!theme?.descripteur) return [];
    return theme.descripteur
        .flatMap((desc) => desc.attendus || [])
        .filter((att) => att.rubrique === 'automatisme' && hasGenerator(att.id));
};

/**
 * Tire une suite de `nbQuestions` exercices dans les générateurs du thème.
 *
 * - attendu pioché au hasard, sans doublon tant que le vivier le permet ;
 * - format aléatoire du générateur, TOUS formats confondus — y compris les
 *   programmes de construction (décision utilisateur 14/07 : tout autoriser ;
 *   ça garantit aussi que « thème affiché jouable » == « tirage possible ») ;
 * - quand l'attribut `difficulte` existera sur les générateurs,
 *   `opts.difficulte` filtrera les formats (signature posée, no-op).
 *
 * @returns {Array} payloads BanqueQuestion (jouables tels quels par Quiz)
 */
export const tirerDefi = (theme, nbQuestions = 5, opts = {}) => {
    // eslint-disable-next-line no-unused-vars
    const { difficulte } = opts; // hook futur — aucun générateur ne l'expose encore
    const vivier = attendusJouables(theme);
    if (!vivier.length) return [];

    const questions = [];
    const attendusUtilises = new Set();

    for (let i = 0; i < nbQuestions; i++) {
        let payload = null;
        for (let essai = 0; essai < 12 && !payload; essai++) {
            // Sans doublon d'attendu tant que possible, sinon on ré-autorise
            const candidats = vivier.filter((a) => !attendusUtilises.has(a.id));
            const pool = candidats.length ? candidats : vivier;
            const att = pool[Math.floor(Math.random() * pool.length)];
            try {
                payload = generateExercice(att.id);
                attendusUtilises.add(att.id);
            } catch {
                // générateur en échec sur ce tirage — on re-tire ailleurs
            }
        }
        if (payload) questions.push(payload);
    }
    return questions;
};

// ── Compteur local (optimistic UI / dégradation sans back) ──────────────
// Le back reste juge : profil.defis_credites_jour écrase toujours ce compteur.

const CLE_COMPTEUR = 'tanoDefisJour';

const jourLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const defisFaitsAujourdhui = () => {
    try {
        const brut = JSON.parse(localStorage.getItem(CLE_COMPTEUR) || 'null');
        return brut?.jour === jourLocal() ? (brut.count || 0) : 0;
    } catch {
        return 0;
    }
};

export const marquerDefiFait = () => {
    try {
        const count = defisFaitsAujourdhui() + 1;
        localStorage.setItem(CLE_COMPTEUR, JSON.stringify({ jour: jourLocal(), count }));
        return count;
    } catch {
        return 0;
    }
};

/**
 * Compteur affiché : la vérité serveur (profil) prime, le local ne sert que
 * d'optimistic UI entre la soumission et le refetch du profil.
 */
export const defisCreditesAffiches = (profil) => {
    const serveur = profil?.defis_credites_jour;
    if (typeof serveur === 'number') return Math.max(serveur, 0);
    return Math.min(defisFaitsAujourdhui(), DEFIS_QUOTA_JOUR);
};

/**
 * Total de défis JOUÉS aujourd'hui (crédités ou non) — pour le plafond 30.
 * max(serveur, local) : `profil.defis_total_jour` (COUNT back, multi-appareils,
 * plafonne naturellement à 30 puisque le back ne stocke plus au-delà) complété
 * par le compteur localStorage (couvre l'écart avant refetch et le mode sans
 * back — le local ne peut que SUR-bloquer sur cet appareil, jamais débloquer).
 */
export const defisTotalAffiche = (profil) => {
    const serveur = profil?.defis_total_jour;
    return Math.max(typeof serveur === 'number' ? serveur : 0, defisFaitsAujourdhui());
};

/** Plafond quotidien atteint → on bloque le lancement (message « victoire »). */
export const defisEpuises = (profil) => defisTotalAffiche(profil) >= DEFIS_MAX_JOUR;
